import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import { useUpdateCompanyMutation, useUpdateVatCompanyMutation } from "../../../../../../features/companyApi";
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice";
import { keycloak } from "../../../../../lib/keycloak";
import { comuneCapMap } from "../../../../../lib/places/cap";
import { regions } from "../../../../../lib/places/italianRegions";
import { provinceComuniMap } from "../../../../../lib/places/listaComuni";
import { useAppDispatch, useAppSelector } from "../../../../../lib/redux/hooks";
import { OptionType } from "../../../../../lib/types";
import { groupBy } from "../../../../../lib/utils";
import { GlobeIcon } from "../../../../../ui/icons/globe";
import Button from "../../../../../ui/molecules/button";
import Input from "../../../../../ui/molecules/input";
import { SelectCustom } from "../../../../../ui/molecules/select";
import Toggle from "../../../../../ui/molecules/toggle";
import { CompanyDTO } from "../../dto";
import { CalculateLocationTooltip } from "./calculateLocationTooltip";
import { CompanyEmails } from "./companyEmails";
import { CompanyPhones } from "./companyPhones";
import { UpdateCompanyFormValues, updateCompanySchema, UpdateVatFormValues, updateVatSchema } from "./validation";

interface Props {
  isInTargetList: boolean
  handleClose: () => void
  company?: CompanyDTO
}

export function EditCompanyForm(props: Props) {
  const { handleClose, company } = props
  const { tab } = useParams()

  const form = useForm<UpdateCompanyFormValues>({
    resolver: zodResolver(updateCompanySchema),
    defaultValues: {},
  });

  const vatForm = useForm<UpdateVatFormValues>({
    resolver: zodResolver(updateVatSchema),
    defaultValues: { vat: company?.vat || "" },
  });

  useEffect(() => {
    if (company) {
      const formValues: Partial<UpdateCompanyFormValues> = {
        revenue: company.revenue,
        phones: company.phones,
        emails: company.emails,
        address: company.address,
        zipCode: company.zipCode,
        city: company.city,
        province: company.province,
        countryRegion: company.countryRegion,
        state: company.state,
        ateco: company.ateco,
        ecommerce: company.ecommerce,
        employeesNumber: company.employeesNumber,
        website: company.website,
        active: company.active,
      };
      form.reset(formValues);
      vatForm.reset({ vat: company?.vat || "" });
    }
  }, [company, form, vatForm]);

  const [updateCompany] = useUpdateCompanyMutation();
  const [updateVatCompany, { isLoading: isUpdatingVat }] = useUpdateVatCompanyMutation();
  const dispatch = useAppDispatch();

  const handleSubmit = async () => {
    try {
      await updateCompany({
        id: company?.id || "",
        data: form.getValues(),
      }).unwrap();
      dispatch(addBannerWithTimeout({
        message: "Contatto aggiornato con successo",
        variant: "success"
      }));
    } catch (error) {
      dispatch(addBannerWithTimeout({
        message: "Errore nell'aggiornamento del contatto",
        variant: "error"
      }));
    }
  };

  const handleVatSubmit = async () => {
    try {
      await updateVatCompany({
        id: company?.id || "",
        vat: vatForm.getValues().vat!,
      }).unwrap();
      dispatch(addBannerWithTimeout({
        message: "Partita IVA aggiornata con successo",
        variant: "success"
      }));
    } catch (error) {
      dispatch(addBannerWithTimeout({
        message: "Errore nell'aggiornamento della Partita IVA",
        variant: "error"
      }));
    }
  };

  const toValue = (v: any) => {
    if (v == null) return undefined;
    if (Array.isArray(v)) return v.map((it) => (it?.value ?? it));
    return v.value ?? v;
  };

  const atecosState = useAppSelector((state) => state.atecos);

  const defaultAtecos = atecosState.findAllAtecosResponse.map((sector) => ({ label: sector.atecoCode + ' - ' + sector.atecoDescription, value: sector.atecoCode }))

  const defaultSectors = groupBy(
    atecosState.findAllAtecosResponse,
    "sector"
  ).map((sector) => ({ label: sector.key, value: sector.key }))

  const defaultCategories = groupBy(
    atecosState.findAllAtecosResponse
    , "category"
  ).map((category) => ({ label: category.key, value: category.key }))

  const [options, setOptions] = useState<{ ateco: OptionType[], sector: OptionType[], category: OptionType[] }>({ ateco: defaultAtecos, sector: defaultSectors, category: defaultCategories })

  useEffect(() => {
    setOptions({ ateco: defaultAtecos, sector: defaultSectors, category: defaultCategories })
  }, [])

  function getFilteredOptions(category: string | null, sector: string | null) {
    const filteredAtecos = atecosState.findAllAtecosResponse.filter((ateco) => {
      return (
        (!category || ateco.category === category) &&
        (!sector || ateco.sector === sector)
      );
    });

    const atecoOptions = filteredAtecos.map((ateco) => ({
      label: ateco.atecoCode + ' - ' + ateco.atecoDescription,
      value: ateco.atecoCode,
    }));

    const sectorOptions = groupBy(filteredAtecos, "sector").map((sector) => ({
      label: sector.key,
      value: sector.key,
    }));

    const categoryOptions = groupBy(filteredAtecos, "category").map((category) => ({
      label: category.key,
      value: category.key,
    }));

    return { atecoOptions, sectorOptions, categoryOptions };
  }

  const ateco = form.watch("ateco");
  const state = form.watch("state");
  const countryRegion = form.watch("countryRegion");
  const province = form.watch("province");
  const zipCode = form.watch("zipCode");

  function handleChangeAtecos(e: string | undefined) {
    const selectedAteco = atecosState.findAllAtecosResponse.find(
      (ateco) => ateco.atecoCode === e
    );

    if (selectedAteco) {
      const atecos = ateco ?? { atecoCode: null, sector: null, category: null, atecoDescription: null };

      atecos.atecoCode = selectedAteco.atecoCode;
      atecos.sector = selectedAteco.sector;
      atecos.category = selectedAteco.category;
      form.setValue("ateco", { ...atecos }, { shouldValidate: true });
    } else {
      form.setValue("ateco", { atecoCode: null, sector: null, category: null, atecoDescription: null }, { shouldValidate: true });
    }

    const { atecoOptions, sectorOptions, categoryOptions } = getFilteredOptions(
      selectedAteco?.category ?? null,
      selectedAteco?.sector ?? null
    );

    setOptions({ ateco: atecoOptions, sector: sectorOptions, category: categoryOptions });
  }

  function handleChangeSectors(e: string | undefined) {
    const atecos = ateco ?? { atecoCode: null, sector: null, category: null, atecoDescription: null };
    const selectedSector = e || null;

    atecos.sector = selectedSector;

    const { atecoOptions, categoryOptions } = e
      ? getFilteredOptions(atecos.category ?? null, selectedSector)
      : { atecoOptions: defaultAtecos, categoryOptions: defaultCategories };


    setOptions({ ...options, ateco: atecoOptions, category: categoryOptions });

    form.setValue("ateco", { ...atecos }, { shouldValidate: true });
  }

  function handleChangeCategories(e: string | undefined) {
    const atecos = ateco ?? { atecoCode: null, sector: null, category: null, atecoDescription: null };
    const selectedCategory = e || null;

    atecos.category = selectedCategory;

    let atecoOptions, sectorOptions;

    if (selectedCategory === null) {
      atecoOptions = defaultAtecos;
      sectorOptions = defaultSectors;

      if (!defaultSectors.map((sector) => sector.value).includes(atecos.sector!)) {
        atecos.sector = null;
      }
    } else {
      const filteredAtecos = atecosState.findAllAtecosResponse.filter(
        (ateco) => ateco.category === selectedCategory
      );
      atecoOptions = groupBy(filteredAtecos, "atecoCode").map((ateco) => ({
        label: ateco.key,
        value: ateco.key,
      }));
      sectorOptions = groupBy(filteredAtecos, "sector").map((sector) => ({
        label: sector.key,
        value: sector.key,
      }));

      if (!sectorOptions.map((sector) => sector.value).includes(atecos.sector!)) {
        atecos.sector = null;
      }
    }

    setOptions({
      ...options,
      ateco: atecoOptions,
      sector: sectorOptions,
    });

    form.setValue("ateco", { ...atecos }, { shouldValidate: true });
  }

  const defaultOptions = [
    { label: "Italia", value: "Italia" },
    { label: "Francia", value: "Francia" },
    { label: "Germania", value: "Germania" },
    { label: "Stati Uniti d'America", value: "Stati Uniti" },
    { label: "Regno Unito", value: "Regno Unito" },
  ];

  const provinceFilters = (region?: string) => { return (region ? regions.find(reg => reg.name === region)?.provinces || [] : regions?.flatMap((regions) => regions.provinces)) }
  const comuniFilters = (province?: string, cap?: string) => {
    if (province) {
      if (cap) {
        return provinceComuniMap.get(province)?.filter(comune => comuneCapMap.get(comune)?.includes(cap.toString())) || []
      }
      return provinceComuniMap.get(province) || []
    } else {
      if (cap) {
        return Array.from(comuneCapMap.entries()).filter(([_, caps]) => caps.includes(cap.toString())).map(([comune, _]) => comune);
      }
      return Array.from(provinceComuniMap.values())?.flatMap((comune) => comune)
    }
  }

  return (
    <div key={0} className="px-6 py-4 flex flex-col gap-4 relative w-full">
      {
        tab !== 'companies' &&
        <Input
          label="Ragione sociale"
          defaultValue={company?.name ?? ""}
          value={company?.name ?? ""}
          disabled
        />
      }
      <div className="flex items-end justify-end gap-2">
        <Controller
          name="vat"
          control={vatForm.control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ""}
              placeholder="Nome"
              label="P. IVA"
              onChangeText={(e) => field.onChange(e)}
              disabled={!(keycloak.hasRealmRole('admin') && !company?.vat)}
              supportingText={vatForm.formState.errors.vat?.message as string | undefined}
              error={!!vatForm.formState.errors.vat}
            />
          )}
        />
        {
          (keycloak.hasRealmRole('admin') && !company?.vat) &&
          <div className={clsx({
            "pb-11": !!vatForm.formState.errors.vat
          })}>
            <Button
              label="Associa P. IVA"
              disabled={!vatForm.watch("vat")}
              onClick={() => {
                vatForm.handleSubmit(handleVatSubmit)();
              }}
              size={"md"}
              iconPosition={"off"}
              variant={"outline"}
              color={"blue"}
            />
          </div>
        }
      </div>
      <CompanyEmails form={form} />
      <CompanyPhones form={form} />
      <Controller
        name="revenue"
        control={form.control}
        render={({ field }) => (
          <NumericFormat
            prefix="€ "
            value={field.value || ''}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            label="Fatturato"
            customInput={Input}
            id={"company-to-confirm-price"}
            key={"company-to-confirm-price"}
            placeholder={"Fatturato"}
            onValueChange={(e) => {
              field.onChange(e.floatValue);
            }}
            error={!!form.formState.errors.revenue}
            supportingText={form.formState.errors.revenue?.message as string | undefined}
          />
        )}
      />
      <Controller
        name="employeesNumber"
        control={form.control}
        render={({ field }) => (
          <NumericFormat
            value={field.value || ''}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={0}
            fixedDecimalScale
            label="Numero dipendenti"
            customInput={Input}
            id={"company-to-confirm-employees"}
            key={"company-to-confirm-employees"}
            placeholder={"Numero dipendenti"}
            onValueChange={(e) => field.onChange(e.floatValue)}
            error={!!form.formState.errors.employeesNumber}
            supportingText={form.formState.errors.employeesNumber?.message as string | undefined}
          />
        )}
      />
      <Controller
        name="ateco.atecoCode"
        control={form.control}
        render={({ field }) => (
          <SelectCustom
            {...field}
            label="Cod. ATECO"
            menuPortalTargetIsNotBody
            onChange={(e) => {
              if (typeof e === 'string' || !e)
                handleChangeAtecos(e)
            }}
            isClearable={true}
            defaultValue={form.getValues("ateco")?.atecoCode || undefined}
            placeholder={"Cerca per ateco"}
            options={options.ateco}
            noOptionsMessage="Non sono presenti ateco."
            error={!!form.formState.errors.ateco?.atecoCode}
            errorLabel={form.formState.errors.ateco?.atecoCode?.message as string | undefined}
          />
        )}
      />
      <Controller
        name="ateco.sector"
        control={form.control}
        render={({ field }) => (
          <SelectCustom
            {...field}
            label="Settore"
            menuPortalTargetIsNotBody
            onChange={(e) => {
              if (typeof e === 'string' || !e)
                handleChangeSectors(e)
            }}
            isClearable={true}
            defaultValue={form.getValues("ateco")?.sector || undefined}
            placeholder={"Cerca per settore"}
            options={options.sector}
            noOptionsMessage="Non sono presenti settori."
            error={!!form.formState.errors.ateco?.sector}
            errorLabel={form.formState.errors.ateco?.sector?.message as string | undefined}
          />
        )}
      />
      <Controller
        name="ateco.category"
        control={form.control}
        render={({ field }) => (
          <SelectCustom
            {...field}
            label="Categoria"
            menuPortalTargetIsNotBody
            onChange={(e) => {
              if (typeof e === 'string' || !e)
                handleChangeCategories(e)
            }}
            isClearable={true}
            defaultValue={form.getValues("ateco")?.category || undefined}
            placeholder={"Cerca per categoria"}
            options={options.category}
            noOptionsMessage="Non sono presenti categorie."
            error={!!form.formState.errors.ateco?.category}
            errorLabel={form.formState.errors.ateco?.category?.message as string | undefined}
          />
        )}
      />
      <div>
        <Controller
          name="ecommerce"
          control={form.control}
          render={({ field }) => (
            <Toggle
              {...field}
              checked={field.value || false}
              placeholder="Aggiungi categoria e-commerce"
            />
          )}
        />
      </div>
      <Controller
        name="address"
        control={form.control}
        render={({ field }) => (
          <Input
            {...field}
            label="Indirizzo"
            value={field.value || ""}
            error={!!form.formState.errors.address}
            supportingText={form.formState.errors.address?.message as string | undefined}
          />
        )}
      />
      <Controller
        name="state"
        control={form.control}
        render={({ field }) => (
          <SelectCustom
            {...field}
            label="Stato"
            isClearable
            menuPortalTargetIsNotBody
            onChange={(e) => field.onChange(toValue(e))}
            noOptionsMessage="Stato non trovato"
            placeholder="Seleziona una nazione..."
            defaultValue={field.value || ""}
            options={defaultOptions}
            error={!!form.formState.errors.state}
            errorLabel={form.formState.errors.state?.message as string | undefined}
          />
        )}
      />
      <div className="flex flex-col gap-3">
        {
          (state === "Italia" || !state) ?
            <>
              <Controller
                name="countryRegion"
                control={form.control}
                render={({ field }) => (
                  <SelectCustom
                    {...field}
                    isClearable
                    label="Regione"
                    menuPortalTargetIsNotBody
                    onChange={(e) => field.onChange(toValue(e))}
                    defaultValue={field.value || ""}
                    placeholder={"Seleziona una regione"}
                    options={regions.map(region => ({ value: region.name, label: region.name }))}
                    error={!!form.formState.errors.countryRegion}
                    errorLabel={form.formState.errors.countryRegion?.message as string | undefined}
                  />
                )}
              />
              <Controller
                name="province"
                control={form.control}
                render={({ field }) => (
                  <SelectCustom
                    isClearable
                    label="Provincia"
                    menuPortalTargetIsNotBody
                    onChange={e => field.onChange(toValue(e))}
                    defaultValue={field.value || ""}
                    placeholder={"Seleziona una città"}
                    options={provinceFilters(countryRegion ?? undefined)?.map((province) => ({
                      value: province.code,
                      label: province.name,
                    }))}
                  />
                )}
              />
              <Controller
                name="city"
                control={form.control}
                render={({ field }) => (
                  <SelectCustom
                    isClearable
                    label="Comune"
                    menuPortalTargetIsNotBody
                    onChange={e => field.onChange(toValue(e))}
                    defaultValue={field.value || ""}
                    placeholder={"Seleziona un comune"}
                    options={comuniFilters(province ?? undefined, zipCode ?? undefined)?.map((comune) => ({
                      value: comune,
                      label: comune,
                    }))}
                  />
                )}
              />

              <Controller
                name="zipCode"
                control={form.control}
                render={({ field }) => (
                  <div className="flex flex-row gap-2 items-end ">
                    <Input
                      {...field}
                      label="Cap"
                      key={'edit-companies-form-zipcode'}
                      placeholder="Inserisci il cap"
                      value={field.value || ""}
                      error={!!form.formState.errors.zipCode}
                      supportingText={form.formState.errors.zipCode?.message as string | undefined}
                    />
                    <CalculateLocationTooltip form={form} />
                  </div>
                )}
              />
            </> :
            <Controller
              name="city"
              control={form.control}
              render={({ field }) => (
                <Input
                  label="Città"
                  key={'edit-companies-form-city'}
                  placeholder="Inserisci la città"
                  {...field}
                  value={field.value || ""}
                  error={!!form.formState.errors.city}
                  supportingText={form.formState.errors.city?.message as string | undefined}
                />
              )}
            />
        }
      </div>
      <Controller
        name="website"
        control={form.control}
        render={({ field }) => (
          <Input
            label="Sito web"
            endIcon={<GlobeIcon color={""} size={0} />}
            {...field}
            value={field.value || ""}
            error={!!form.formState.errors.website}
            supportingText={form.formState.errors.website?.message as string | undefined}
          />
        )}
      />
      <div className={clsx("py-6 justify-end items-center gap-2 flex")}>
        <Button
          size={"sm"}
          iconPosition={"off"}
          variant={"outline"}
          color={"blue"}
          label="Annulla"
          onClick={() => handleClose()}
        />
        <Button
          size={"sm"}
          iconPosition={"off"}
          variant={"solid"}
          color={"blue"}
          label="Modifica contatto"
          disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
          onClick={() => form.handleSubmit(handleSubmit)()}
        />
      </div>
    </div>
  );
}
